import React, { useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { CircleFadingArrowUp, MenuIcon, ChevronDownIcon, Plus, GalleryVerticalEnd, ChevronRightIcon, SendHorizonal, Paperclip, LoaderCircle, ArrowDownIcon, PlusCircle, CircleArrowUp, ShieldCheck, TestTube, SquareFunction, TestTubeDiagonal, ChevronDown, ChevronUpIcon, ChevronUp, ArrowUp } from 'lucide-react';
import ThinkingIndicator from './ThinkingIndicator';
import DefaultMessageResponse from './DefaultMessageResponse';

const CenteredChatLayout = ({
    // Props for messages and state
    messages,
    isLoadingOlder,
    hasMoreMessages,
    isProcessing,
    userHasScrolled,
    input,
    setInput,
    handleSubmit,
    handleKeyDown,
    handleScroll,
    loadOlderMessages,
    scrollToBottom,
    handleCopy,
    handleOpenTab,
    onSuggestionClick,
    isNewTabOpened,
    setIsNewTabOpened,
    latestMessageId,
    onPreviewDetected,
    selectedCollection,
    generatedTestScenarios,
    collectionsDialogRef,
    collectionsButtonRef,
    messagesContainerRef,
    messagesEndRef,
    chatId,
    showEnvMemoryInput,
    saveInEnvironment,
    setSaveInEnvironment,
    saveInMemory,
    setSaveInMemory,
    handleTryAgain,
    modelsDialogRef,
    selectedAIModel,
    aiModels,
}) => {
    // Suggestions from FullScreenDefaultTab
    const suggestions = [
        {
            icon: <TestTube size={13} />,
            title: "Unit Test",
            description: "Generate endpoint-level unit tests for each route in the current API collection. For every endpoint, include: \n 1. One positive test case with valid inputs \n 2. One negative test case with invalid or missing parameters \n 3. One boundary-value case (e.g., max-length strings or edge-case numbers)"
        },
        {
            icon: <SquareFunction size={13} />,
            title: "Functional Test",
            description: "Write end-to-end functional tests that simulate real user flows across the API. Automatically generate any prerequisite data and ensure proper cleanup post-execution."
        },
        {
            icon: <TestTubeDiagonal size={13} />,
            title: "OWASP Top 10 Vulnerabilities",
            description: "Generate security test cases that cover the OWASP API Top 10 vulnerabilities—including, but not limited to: Broken Authentication, Broken Object Level Authorization, Injection (SQL, NoSQL, Command), Security Misconfiguration, Sensitive Data Exposure, Broken Access Control, Cross-Site Scripting (XSS), and other API-specific risks. Each test should attempt a realistic exploit and verify the API's defensive response."
        },
        {
            icon: <ShieldCheck size={13} />,
            title: "Penetration Test",
            description: "Perform a penetration test on the provided API endpoints. Simulate real-world attack scenarios such as SQL injection, cross-site scripting, authentication bypass, insecure direct object access, broken access control, and rate limiting abuse. Actively probe the endpoints with malicious inputs and behaviors to identify potential security vulnerabilities and weaknesses in the system's defenses.",
        },
    ];

    const hasPreviewContent = (message) =>
        (message.generated_test_scenarios && message.generated_test_scenarios.length > 0) ||
        (message.selected_collection !== undefined) ||
        (message.current_test_scenario !== null) ||
        (message.current_test_case_set !== null);

    const previewDetectedRef = useRef(false);
    useEffect(() => {
        if (!previewDetectedRef.current && (
            (selectedCollection && selectedCollection.length > 0) ||
            (generatedTestScenarios && generatedTestScenarios.length > 0) ||
            (messages && messages.some(hasPreviewContent))
        )) {
            previewDetectedRef.current = true;
            if (onPreviewDetected) onPreviewDetected();
        }
    }, [messages, selectedCollection, generatedTestScenarios, onPreviewDetected]);

    return (
        <div className="w-full h-[calc(100vh-51px)] bgSecondary flex flex-col">
            {/* Header */}
            <div className="w-full mx-auto">
                {/* Centered Messages Container */}
                <div className="flex-1 w-full flex flex-col justify-center items-center">
                    {!chatId && messages.length === 0 ? (
                        <div className="h-[calc(100vh-53px)] bgSecondary flex flex-col items-end justify-center  px-6 mr-2 rounded-xl">
                            <div className="w-full max-w-3xl mx-auto flex flex-col items-end">
                                {/* Headline */}
                                <p className="text-center mb-2 tracking-tighter font-normal text-[24px] text-[color:var(--text-primary)] custom_font">
                                    What can I help you to test?
                                </p>
                                {/* Input Bar */}
                                <form
                                    onSubmit={(e) => e.preventDefault()}
                                    className="w-full mx-auto flex items-center rounded-lg overflow-hidden relative"
                                >
                                    <div className="bgPrimary customBorder rounded-2 w-full relative z-10">
                                        <InputTextarea
                                            value={input}
                                            onChange={(e) => { setInput(e.target.value) }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSubmit();
                                                }
                                            }}
                                            placeholder="Write what do you want to test..."
                                            className="w-full rounded-xl bg-transparent text-[var(--text-primary)] chat-custom-scrollbar shadow-none pr-11 !border-none"
                                            rows={4}
                                            autoResize
                                            style={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                minHeight: "48px",
                                                maxHeight: "120px"
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={!input || !input.trim()}
                                            icon={
                                                <ArrowUp size={16} />
                                            }
                                            className="absolute right-3 bottom-2 w-auto h-8 bg-transparent border !border-[var(--borderColor)] text-[var(--text-primary)] px-1.5 py-0 m-1 !rounded-md shadow-none transition-all duration-150"
                                        />
                                        <Button
                                            type="button"
                                            id="collection-dropdown-wrapper"
                                            ref={collectionsButtonRef}
                                            onClick={(e) => collectionsDialogRef.current.toggle(e)}
                                            aria-label='Add Collection'
                                            className="absolute left-2 bottom-2 w-auto h-8 bgPrimary hover:!bg-[var(--background-secondary)] shadow-none outline-none !border-none text-[var(--text-primary)] px-2 py-3 m-1 !rounded transition-all duration-150"
                                        >
                                            <span className='mr-2 fs11'>Add a Collection</span> <span> <ChevronDown size={12} /> </span>
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={(e) => modelsDialogRef.current.toggle(e)}
                                            aria-label='AI Model'
                                            icon={aiModels.find((model) => model.value === selectedAIModel)?.icon}
                                            className="flex items-center absolute left-[130px] bottom-2 w-auto h-8 bgPrimary hover:!bg-[var(--background-secondary)] border-none text-[var(--text-primary)] px-2 py-1 m-1 !rounded transition-all duration-150 outline-none shadow-none"
                                        >
                                            <span className='ml-1 fs11'>{aiModels.find((model) => model.value === selectedAIModel)?.displayName}</span> <ChevronDown size={12} className='ml-1' />
                                        </Button>
                                    </div>
                                </form>
                                <div className="my-6">
                                    <div className='text-center fs13 text-[var(--text-secondary)]'>
                                        <p>Popular Test Types</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2 w-full">
                                        {suggestions.map((suggestion, index) => {
                                            return (
                                                <button
                                                    key={index}
                                                    onClick={() => setInput(suggestion.description)}
                                                    className="flex items-center justify-center gap-1 bgPrimary hover:bg-[var(--hover-bg)] px-3 py-1 !rounded shadow-none transition-all w-fit border !border-[var(--borderColor)] font-normal text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                                >
                                                    {suggestion.icon}
                                                    <span className="fs12">{suggestion.title}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="w-full overflow-y-auto max-h-[calc(100vh-170px)] py-5 h-[calc(100vh-170px)] chatscreen-custom-scrollbar"
                        >
                            <div
                                className="w-full max-w-4xl mx-auto"
                                ref={messagesContainerRef}
                                onScroll={handleScroll}
                            >
                                {messages.length > 4 && hasMoreMessages && (
                                    <div className="w-full flex justify-center mb-5">
                                        <Button onClick={loadOlderMessages} disabled={isLoadingOlder} className="bg-transparent text-[var(--text-primary)] hover:bg-[var(--hover-bg)] border-none p-2 !rounded-lg">
                                            <CircleFadingArrowUp size={12} className="mr-2" /> {isLoadingOlder ? "Loading..." : "Load Older Messages"}
                                        </Button>
                                    </div>
                                )}
                                {isLoadingOlder && (
                                    <div className="text-center py-2 mb-5">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--text-primary)] mx-auto"></div>
                                    </div>
                                )}
                                {!hasMoreMessages && messages.length > 0 && (
                                    <div className="text-center py-2 text-[var(--text-tertiary)] text-sm mb-5">
                                        No more messages to load
                                    </div>
                                )}

                                <>
                                    {messages.map((message, index) => (
                                        <DefaultMessageResponse
                                            key={message.id || index}
                                            message={message}
                                            latestMessageId={latestMessageId}
                                            index={index}
                                            handleCopy={handleCopy}
                                            handleOpenTab={handleOpenTab}
                                            chatId={chatId}
                                            onSuggestionClick={onSuggestionClick}
                                            isNewTabOpened={isNewTabOpened}
                                            setIsNewTabOpened={setIsNewTabOpened}
                                            handleTryAgain={handleTryAgain}
                                        />
                                    ))}
                                    <div ref={messagesEndRef} data-messages-end className="h-1" />
                                </>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Section - Only show when there are messages */}
                {messages.length > 0 && (
                    <div className="w-full max-w-4xl mx-auto bg-[var(--agent-avatar)] border-t !border-[var(--borderColor)] flex-none relative z-10 rounded-2xl">
                        {userHasScrolled && (
                            <Button
                                onClick={scrollToBottom}
                                className="bg-[var(--button-primary)] hover:bg-[var(--button-primary-hover)] border-none p-2 !rounded-full shadow-md absolute bottom-48 left-1/2 transform -translate-x-1/2"
                            >
                                <ArrowDownIcon size={20} />
                            </Button>
                        )}
                        {isProcessing && (
                            <ThinkingIndicator />
                        )}

                        {!isProcessing && showEnvMemoryInput && (
                            <div className="flex items-center gap-4 mb-2 px-2">
                                <span className='text-[var(--text-secondary)]'>Save In: </span>
                                <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={saveInEnvironment}
                                        onChange={(e) => setSaveInEnvironment(e.target.checked)}
                                        className="w-4 h-4 rounded border-[var(--borderColor)] bgSecondary text-[var(--primary-color)] focus:ring-[var(--primary-color)] focus:ring-offset-[var(--background-secondary)]"
                                    />
                                    Environment
                                </label>
                                <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={saveInMemory}
                                        onChange={(e) => setSaveInMemory(e.target.checked)}
                                        className="w-4 h-4 rounded border-[var(--borderColor)] bgSecondary text-[var(--primary-color)] focus:ring-[var(--primary-color)] focus:ring-offset-[var(--background-secondary)]"
                                    />
                                    Memory
                                </label>
                            </div>
                        )}
                        <form
                            onSubmit={(e) => e.preventDefault()}
                            className="w-full mx-auto flex items-center rounded-lg overflow-hidden relative"
                        >
                            <div className="bgPrimary customBorder rounded-2 w-full relative z-10">
                                <InputTextarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Write what do you want to test..."
                                    className="w-full rounded-xl bg-transparent text-[var(--text-primary)] chat-custom-scrollbar shadow-none pr-11 !border-none"
                                    rows={4}
                                    autoResize
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        minHeight: "48px",
                                        maxHeight: "105px"
                                    }}
                                />
                                <Button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={!input || !input.trim() || isProcessing}
                                    icon={
                                        isProcessing ? (
                                            <LoaderCircle size={16} className="animate-spin" />
                                        ) : (
                                            <ArrowUp size={16} />
                                        )
                                    }
                                    className="absolute right-3 bottom-2 w-auto h-8 bg-transparent border !border-[var(--borderColor)] text-[var(--text-primary)] px-1.5 py-0 m-1 !rounded-md shadow-none transition-all duration-150"
                                />
                                <Button
                                    type="button"
                                    id="collection-dropdown-wrapper"
                                    ref={collectionsButtonRef}
                                    onClick={(e) => collectionsDialogRef.current.toggle(e)}
                                    aria-label='Add Collection'
                                    className="absolute left-2 bottom-2 w-auto h-8 bgPrimary hover:!bg-[var(--background-secondary)] !border-none shadow-none outline-none text-[var(--text-primary)] px-2 py-3 m-1 !rounded transition-all duration-150"
                                >
                                    <span className='mr-2 fs11'>Add a Collection</span> <span> <ChevronDown size={12} /> </span>
                                </Button>
                                <Button
                                    type="button"
                                    onClick={(e) => modelsDialogRef.current.toggle(e)}
                                    aria-label='AI Model'
                                    icon={aiModels.find((model) => model.value === selectedAIModel)?.icon}
                                    className="fs10 flex items-center absolute left-[130px] bottom-2 w-auto h-8 bgPrimary hover:!bg-[var(--background-secondary)] border-none text-[var(--text-primary)] px-2 py-1 m-1 !rounded transition-all duration-150 outline-none shadow-none"
                                >
                                    <span className='ml-1 fs11'>{aiModels.find((model) => model.value === selectedAIModel)?.displayName}</span> <ChevronDown size={12} className='ml-1' />
                                </Button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CenteredChatLayout; 